import { Badge } from "@/components/ui/badge";
import { Gauge } from "lucide-react";
import type { ProposedFix } from "@/types/technicalAudit";

interface ConfidenceBadgeProps {
	fix: ProposedFix | null | undefined;
}

type Tone = { text: string; bg: string; border: string };

function toneFor(label: string): Tone {
	switch (label) {
		case "high":
			return { text: "text-teal-dark", bg: "bg-teal-light", border: "border-teal-dark/30" };
		case "medium":
			return { text: "text-coral-dark", bg: "bg-coral-light", border: "border-coral/30" };
		case "low":
		case "none":
			return {
				text: "text-destructive",
				bg: "bg-destructive/5",
				border: "border-destructive/30",
			};
		default:
			return { text: "text-muted-foreground", bg: "bg-muted", border: "border-border" };
	}
}

/**
 * A2: shows the AI confidence / relevance score next to a generated fix. High =
 * the crawl evidence clearly supports the fix; low = the model had thin context
 * and was guessing, so a human should look harder before applying. The score is
 * produced by an INDEPENDENT judge model (not the generator grading itself).
 */
export function ConfidenceBadge({ fix }: ConfidenceBadgeProps) {
	const score = fix?.confidence_score;
	const label = fix?.confidence_label;
	if (typeof score !== "number" || !label) return null;

	const tone = toneFor(label);
	const labelText = label.charAt(0).toUpperCase() + label.slice(1);

	const judge = fix?.confidence_source?.startsWith("judge:")
		? fix.confidence_source.slice("judge:".length)
		: null;
	const baseTip =
		"How likely this fix is correct given the crawl evidence. Lower scores mean thin context or a judgment call — review closely before applying.";
	const tip = [
		fix?.confidence_reasoning ? `Why: ${fix.confidence_reasoning}` : null,
		judge ? `Scored independently by ${judge}` : null,
		baseTip,
	]
		.filter(Boolean)
		.join("\n\n");

	return (
		<Badge
			variant="outline"
			className={`gap-1 ${tone.bg} ${tone.text} ${tone.border}`}
			title={tip}
		>
			<Gauge className="h-3 w-3" />
			{labelText} confidence · {score}%
		</Badge>
	);
}
